import { Boundaries } from "@planzone/core";
import { ResourcesListingFilter, resourcesListingFilter } from "@planzone/dto";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

// The resources listed by the Resources section of the filters, apart from the members pages listings the
// same pages show. Kept between two openings, marked as dirty by the changes of the resources listed so the
// section reloads them, as the time sheets do.
const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  resources: [],
  filter: resourcesListingFilter,
  totalPages: 0,
  projectUuid: null,
  isDirty: false,
};

function isSameList(
  state: State,
  filter: ResourcesListingFilter,
  projectUuid: string | null,
): boolean {
  return filter.name === state.filter.name && projectUuid === state.projectUuid;
}

function handleRequest(
  state: State,
  { project, filter }: Boundaries["retrieveFilterResources"]["request"],
): State {
  const projectUuid = project?.uuid ?? null;

  // A dirty list reloaded stays shown until its first page comes back.
  return filter.page === 0
    ? {
        ...state,
        resources: isSameList(state, filter, projectUuid)
          ? state.resources
          : [],
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
        totalPages: isSameList(state, filter, projectUuid)
          ? state.totalPages
          : 0,
        projectUuid,
      }
    : {
        ...state,
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
      };
}

function handleSuccess(
  state: State,
  {
    project,
    resources,
    filter,
    totalPages,
  }: Boundaries["retrieveFilterResources"]["success"],
): State {
  // A late page of a replaced search would be appended to the new list.
  if (
    filter.page !== state.filter.page - 1 ||
    !isSameList(state, filter, project?.uuid ?? null)
  )
    return state;

  return {
    ...state,
    resources:
      filter.page === 0 ? resources : [...state.resources, ...resources],
    request: setRequestStateToSuccess(),
    totalPages,
    isDirty: false,
  };
}

function handleFailure(
  state: State,
  { project, filter }: Boundaries["retrieveFilterResources"]["failure"],
): State {
  if (
    filter.page !== state.filter.page - 1 ||
    !isSameList(state, filter, project?.uuid ?? null)
  )
    return state;

  return { ...state, request: setRequestStateToFailure(), isDirty: false };
}

function markAsDirty(state: State): State {
  return { ...state, isDirty: true };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveFilterResourcesRequested]: handleRequest,
    [events.retrieveFilterResourcesSucceeded]: handleSuccess,
    [events.retrieveFilterResourcesFailed]: handleFailure,
    [events.addPlanzoneMaterialResourceSucceeded]: markAsDirty,
    [events.addPlanzoneVirtualResourceSucceeded]: markAsDirty,
    [events.inviteMembersSucceeded]: markAsDirty,
    [events.removeResourceFromPlanzoneSucceeded]: markAsDirty,
    [events.addProjectMaterialResourceSucceeded]: markAsDirty,
    [events.addProjectVirtualResourceSucceeded]: markAsDirty,
    [events.addPlanzoneResourceToProjectSucceeded]: markAsDirty,
    [events.addPlanzoneResourcesToProjectSucceeded]: markAsDirty,
    [events.addEveryoneToProjectSucceeded]: markAsDirty,
    [events.addPlanzoneResourceToProjectsSucceeded]: markAsDirty,
    [events.removeResourceFromProjectSucceeded]: markAsDirty,
    [events.editResourceSucceeded]: markAsDirty,
    [events.changeProfilePictureSucceeded]: markAsDirty,
    [events.activateResourceSucceeded]: markAsDirty,
    [events.disableResourceSucceeded]: markAsDirty,
    [events.activateProjectResourceSucceeded]: markAsDirty,
    [events.disableProjectResourceSucceeded]: markAsDirty,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
